import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { HelmetProvider } from 'react-helmet-async';

import Navbar from "./components/Navbar";
import RequireAuth from "./components/RequireAuth";
import PatientLayout from "./components/PatientLayout";

// Wrapper for Patient Routes
const PatientRoute = ({ children }) => (
  <RequireAuth>
    <PatientLayout>{children}</PatientLayout>
  </RequireAuth>
);

// Lazy Loaded Components
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const PatientDetail = lazy(() => import("./pages/PatientDetail"));
const PrescriptionForm = lazy(() => import("./pages/PrescriptionForm"));
const PatientHistoryPage = lazy(() => import("./pages/PatientHistoryPage"));
const VisitViewPage = lazy(() => import("./pages/VisitViewPage"));
const PrescriptionSuccessPage = lazy(() => import("./pages/PrescriptionSuccessPage"));
const DoctorDashboard = lazy(() => import("./pages/DoctorDashboard"));
const NewLandingPage = lazy(() => import("./pages/NewLandingPage"));
const PatientLogin = lazy(() => import("./pages/PatientLogin"));
const PatientPortal = lazy(() => import("./pages/PatientPortal"));
const DoctorProfile = lazy(() => import("./pages/DoctorProfile"));
const PatientProfile = lazy(() => import("./pages/PatientProfile"));
const BookVisit = lazy(() => import("./pages/BookVisit"));
const Appointments = lazy(() => import(/* webpackChunkName: "Appointments" */ "./pages/Appointments"));
const DoctorAvailability = lazy(() => import("./pages/DoctorAvailability"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const FamilyManager = lazy(() => import("./pages/FamilyManager"));
const PatientVisitDetail = lazy(() => import("./pages/PatientVisitDetail"));
const PendingApproval = lazy(() => import("./pages/PendingApproval"));
const BookAppointment = lazy(() => import("./pages/BookAppointment"));

// BMU Portal
const BMULayout = lazy(() => import("./components/BMULayout"));
const BMUDashboard = lazy(() => import("./pages/BMUDashboard"));
const BMUHeadDashboard = lazy(() => import("./pages/BMUHeadDashboard"));
const BMURegisterPatient = lazy(() => import("./pages/BMURegisterPatient"));
const BMUPatientHistory = lazy(() => import("./pages/BMUPatientHistory"));
const BMUVisitForm = lazy(() => import("./pages/BMUVisitForm"));
const BMUInvestigations = lazy(() => import("./pages/BMUInvestigations"));
const BMUDischargeForm = lazy(() => import("./pages/BMUDischargeForm"));

// BMU route wrapper: RequireAuth + BMULayout
const BMURoute = ({ children }) => (
  <RequireAuth>
    <BMULayout>{children}</BMULayout>
  </RequireAuth>
);

// Loading Spinner for Suspense
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-600"></div>
  </div>
);

function LayoutWithConditionalNavbar({ children }) {
  const location = useLocation();
  // Hide default navbar on:
  // - Landing page (has its own custom navbar)
  // - Patient routes that have their own PatientNavbar (portal, profile, book)
  const hideNavbar =
    location.pathname === "/" ||
    location.pathname === "/login" ||
    location.pathname === "/register" ||
    location.pathname === "/forgot-password" ||
    location.pathname === "/verify-email" ||
    location.pathname.startsWith("/patient/portal") ||
    location.pathname.startsWith("/patient/profile") ||
    location.pathname.startsWith("/patient/book") ||
    location.pathname.startsWith("/patient/visit") ||
    location.pathname.startsWith("/patient/history") ||
    location.pathname.startsWith("/patient/family-manager") ||
    location.pathname.startsWith("/emergency-access") ||
    location.pathname.startsWith("/bmu") ||
    location.pathname === "/doctor/pending-approval";

  return (
    <>
      <Toaster
        position="top-center"
        reverseOrder={false}
        toastOptions={{
          style: {
            background: '#333',
            color: '#fff',
            borderRadius: '10px',
            padding: '16px',
            fontSize: '14px',
            maxWidth: '500px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          },
          success: {
            style: {
              background: '#065f46', // Dark emerald
              color: '#ecfdf5',
              border: '1px solid #059669',
            },
            iconTheme: {
              primary: '#34d399',
              secondary: '#065f46',
            },
          },
          error: {
            style: {
              background: '#7f1d1d', // Dark red
              color: '#fef2f2',
              border: '1px solid #b91c1c',
            },
            iconTheme: {
              primary: '#f87171',
              secondary: '#7f1d1d',
            },
          },
        }}
      />
      {!hideNavbar && <Navbar />}
      {children}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <LayoutWithConditionalNavbar>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<NewLandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/patient/login" element={<PatientLogin />} />

              {/* Patient Portal (public but user-validated) */}
              <Route path="/patient/portal" element={<PatientRoute><PatientPortal /></PatientRoute>} />
              <Route path="/patient/visit/:id" element={<PatientRoute><PatientVisitDetail /></PatientRoute>} />
              <Route path="/patient/history" element={<PatientRoute><PatientHistoryPage /></PatientRoute>} />

              {/* Protected Routes */}
              <Route
                path="/dashboard"
                element={
                  <RequireAuth>
                    <Dashboard />
                  </RequireAuth>
                }
              />
              <Route
                path="/patient/new"
                element={
                  <RequireAuth>
                    <PatientDetail />
                  </RequireAuth>
                }
              />
              {/* Note: /patient/:id might conflict with other patient routes if not careful, but looks okay here as it's separate from /patient/portal etc */}
              <Route
                path="/patient/:id"
                element={
                  <RequireAuth>
                    <PatientDetail />
                  </RequireAuth>
                }
              />
              <Route
                path="/patient/:id/history"
                element={
                  <RequireAuth>
                    <PatientHistoryPage />
                  </RequireAuth>
                }
              />

              {/* Other Patient Routes that need Layout */}
              <Route path="/patient/profile" element={<PatientRoute><PatientProfile /></PatientRoute>} />
              <Route path="/patient/family-manager" element={<PatientRoute><FamilyManager /></PatientRoute>} />
              <Route path="/patient/book" element={<PatientRoute><BookVisit /></PatientRoute>} />
              <Route path="/patient/book/:doctorId" element={<PatientRoute><BookAppointment /></PatientRoute>} />

              <Route
                path="/prescribe/:id"
                element={
                  <RequireAuth>
                    <PrescriptionForm />
                  </RequireAuth>
                }
              />
              <Route
                path="/visit/:id"
                element={
                  <RequireAuth>
                    <VisitViewPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/prescription/success/:visitId"
                element={
                  <RequireAuth>
                    <PrescriptionSuccessPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/analytics"
                element={
                  <RequireAuth>
                    <DoctorDashboard />
                  </RequireAuth>
                }
              />

              <Route
                path="/doctor/pending-approval"
                element={
                  <RequireAuth>
                    <PendingApproval />
                  </RequireAuth>
                }
              />
              <Route
                path="/doctor/profile"
                element={
                  <RequireAuth requireDoctorApproval={false}>
                    <DoctorProfile />
                  </RequireAuth>
                }
              />

              <Route
                path="/appointments"
                element={
                  <RequireAuth>
                    <Appointments />
                  </RequireAuth>
                }
              />
              <Route
                path="/doctor/availability"
                element={
                  <RequireAuth>
                    <DoctorAvailability />
                  </RequireAuth>
                }
              />
              <Route
                path="/admin"
                element={
                  <RequireAuth allowedRoles={["admin"]}>
                    <AdminDashboard />
                  </RequireAuth>
                }
              />

              {/* BMU Portal routes */}
              <Route path="/bmu/dashboard" element={<BMURoute><BMUDashboard /></BMURoute>} />
              <Route path="/bmu/head-dashboard" element={<BMURoute><BMUHeadDashboard /></BMURoute>} />
              <Route path="/bmu/register" element={<BMURoute><BMURegisterPatient /></BMURoute>} />
              <Route path="/bmu/patient/:id" element={<BMURoute><BMUPatientHistory /></BMURoute>} />
              <Route path="/bmu/patient/:id/visit/new" element={<BMURoute><BMUVisitForm /></BMURoute>} />
              <Route path="/bmu/patient/:id/visit/:visitId" element={<BMURoute><BMUVisitForm /></BMURoute>} />
              <Route path="/bmu/patient/:id/investigations" element={<BMURoute><BMUInvestigations /></BMURoute>} />
              <Route path="/bmu/patient/:id/discharge" element={<BMURoute><BMUDischargeForm /></BMURoute>} />
            </Routes>
          </Suspense>
        </LayoutWithConditionalNavbar>
      </Router>
    </HelmetProvider >
  );
}

export default App;
